import { getCsrfToken } from "@/lib/workspace-api"
import type { LibrarySketchItem } from "@/lib/library-api"
import type { CanvasGenerationRow } from "@/types/canvas"

export type SketchEditEnqueueResult = {
  sketch_edit_id: number
  generation_job_id: number
  generation?: CanvasGenerationRow
}

export type SketchEditShowResult = {
  status: string
  signed_id?: string | null
  url?: string | null
  error?: string | null
}

export async function postSketchEdit(
  projectId: number,
  input: {
    image: Blob
    prompt: string
    seed?: number
    filename?: string
    aspect_ratio?: string
    shot_id?: number
    sketch_shot_tab?: "storyboard" | "shooting_board"
    canvas_id?: number
    source_generation_id?: number
    character_id?: number
    sketch_character_target?: "portrait" | "reference" | "emotion" | "outfit"
    view?: string
    sheet_scope?: "full_body" | "headless"
    outfit_id?: number
    prop_id?: number
    sketch_prop_target?: "front_view" | "reference"
    state_id?: number
    location_id?: number
    variant_id?: number
    facing?: string
  },
): Promise<SketchEditEnqueueResult> {
  const form = new FormData()
  form.append("image", input.image, input.filename ?? "markup.png")
  form.append("prompt", input.prompt)
  if (input.seed != null) form.append("seed", String(input.seed))
  if (input.aspect_ratio) form.append("aspect_ratio", input.aspect_ratio)
  if (input.shot_id != null) form.append("shot_id", String(input.shot_id))
  if (input.sketch_shot_tab) form.append("sketch_shot_tab", input.sketch_shot_tab)
  if (input.canvas_id != null) form.append("canvas_id", String(input.canvas_id))
  if (input.source_generation_id != null) form.append("source_generation_id", String(input.source_generation_id))
  if (input.character_id != null) form.append("character_id", String(input.character_id))
  if (input.sketch_character_target) form.append("sketch_character_target", input.sketch_character_target)
  if (input.view) form.append("view", input.view)
  if (input.sheet_scope) form.append("sheet_scope", input.sheet_scope)
  if (input.outfit_id != null) form.append("outfit_id", String(input.outfit_id))
  if (input.prop_id != null) form.append("prop_id", String(input.prop_id))
  if (input.sketch_prop_target) form.append("sketch_prop_target", input.sketch_prop_target)
  if (input.state_id != null) form.append("state_id", String(input.state_id))
  if (input.location_id != null) form.append("location_id", String(input.location_id))
  if (input.variant_id != null) form.append("variant_id", String(input.variant_id))
  if (input.facing) form.append("facing", input.facing)
  form.append("authenticity_token", getCsrfToken())

  const res = await fetch(`/projects/${projectId}/workspace/sketch/edits`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: form,
    credentials: "same-origin",
  })

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string
    sketch_edit_id?: number
    generation_job_id?: number
    generation?: CanvasGenerationRow
  }

  if (!res.ok || !data.ok || !data.sketch_edit_id || !data.generation_job_id) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }

  return {
    sketch_edit_id: data.sketch_edit_id,
    generation_job_id: data.generation_job_id,
    ...(data.generation ? { generation: data.generation } : {}),
  }
}

export async function fetchSketchEdit(projectId: number, editId: number): Promise<SketchEditShowResult> {
  const res = await fetch(`/projects/${projectId}/workspace/sketch/edits/${editId}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "same-origin",
  })

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    status?: string
    signed_id?: string | null
    url?: string | null
    error?: string | null
  }

  if (!res.ok || !data.status) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }

  return {
    status: data.status,
    signed_id: data.signed_id,
    url: data.url,
    error: data.error,
  }
}

export async function promoteSketchEditToLibrary(projectId: number, editId: number): Promise<LibrarySketchItem> {
  const res = await fetch(`/projects/${projectId}/workspace/sketch/edits/${editId}/promote_to_library`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "same-origin",
  })

  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string
    reference?: LibrarySketchItem
  }

  if (!res.ok || !data.ok || !data.reference) {
    throw new Error(data.error ?? `HTTP ${res.status}`)
  }

  return data.reference
}
